
import { IItemCacheThunkAction, IItemCacheDispatch } from '../../store/actions/ItemCacheActions';
import { demandItems } from '../demandItems';
import { observeItemsUntilResolved } from '../observeItems';
import { IItemCache } from '../../models/IItemCacheObserver';
import { defaultChildItemSetFacet, childItemsFacet, pageFacet } from '../../facets/SetFacets';
import { IAsyncActionCreator, createAsyncActionCreator } from '../../store/actions/createActionCreator';
import { folderFacet, fileNameFacet, fileSizeFacet } from '../../facets/ItemFacets';
import { uploadItemsOperation, IUploadItemOptions } from '../uploadItems';
import { createFolderOperation } from '../createFolder';
import {
  createdItemFacet,
  progressPhaseFacet,
  ProgressPhase,
  destinationItemFacet,
  sourceItemFacet,
  progressSizeFacet,
  cancelSourceFacet
} from '../../facets/ProgressFacets';
import { startOperation, cancelOperation } from '../../store/actions/implementOperation';
import { trackItemProgress, progressRootItemKey, progressAddon } from '../progress';
import { resolveItems } from '../getItems';
import { ArrayConcatIfNotMatchKeyPatch, Delete } from '../../utilities/Graft';
import { pack } from '../../models/Facet';
import { isGroupFacet } from '../../facets/GroupFacets';
import { CancelToken, CancelSource } from '../../utilities/async/CancelError';

import { polyfill } from '../../utilities/symbol';
import { waitForAll } from '../../utilities/async/all';
import { readItems } from '../readItems';
import { entryBatchFacet } from '../../facets/FileSystemFacets';
import { errorFacet } from '../../facets/DemandFacets';
import { getUploadProgressKey } from './UploadProgressKeyFacet';
import { registerItemDemands, removeItemDemands } from '../pruneItems';

polyfill();

/**
 * Props passed to an `onItemQueued` callback.
 * @public
 */
export interface IOnItemQueuedProps {
  progressItemKey: string;
}

/**
 * Props passed to an `onItemUploaded` callback.
 * @public
 */
export interface IOnItemUploadedProps {
  progressItemKey: string;
}

/**
 * Props passed to an 'onItemFailed' callback.
 * @public
 */
export interface IOnItemFailedProps {
  progressItemKey: string;
}

/**
 * Props passed on an `onEntryBatchCreated` callback.
 * @public
 */
export interface IOnEntryBatchCreatedProps {
  /**
   * The key for the progress item created for the upload batch.
   */
  progressItemKey: string;
}

/**
 * Props passed to an `onEntryBatchUploaded` callback.
 * @public
 */
export interface IOnEntryBatchUploadedProps {
  /**
   * The key for the progress item created for the upload batch.
   */
  progressItemKey: string;
}

/**
 * Props passed to an `onEntryBatchUploaded` callback.
 * @public
 */
export interface IOnEntryBatchFailedProps {
  /**
   * The key for the progress item created for the upload batch.
   */
  progressItemKey: string;
  error: Error;
}

/**
 * @public
 */
export interface IUploadCallbacks {
  onItemQueued: (props: IOnItemQueuedProps) => void;
  onItemUploaded: (props: IOnItemUploadedProps) => void;
  onItemFailed: (props: IOnItemFailedProps) => void;
  onEntryBatchCreated: (props: IOnEntryBatchCreatedProps) => void;
  onEntryBatchUploaded: (props: IOnEntryBatchUploadedProps) => void;
  onEntryBatchFailed: (props: IOnEntryBatchFailedProps) => void;
}

const EMPTY_CALLBACK = (): void => { /* Nothing */ };

const EMPTY_CALLBACKS: IUploadCallbacks = {
  onItemQueued: EMPTY_CALLBACK,
  onItemUploaded: EMPTY_CALLBACK,
  onItemFailed: EMPTY_CALLBACK,
  onEntryBatchCreated: EMPTY_CALLBACK,
  onEntryBatchUploaded: EMPTY_CALLBACK,
  onEntryBatchFailed: EMPTY_CALLBACK
};

export interface IUploadEntryBatchPayload {
  parentItemKey: string;
  sourceEntryKey: string;
  callbacks?: IUploadCallbacks;
  cancelToken?: CancelToken;
  options?: IUploadItemOptions;
}

/**
 * Creates an action which, when dispatched, uploads the items from an entry batch into
 * a target item, creating folders and uploading files as needed.
 * The source folders are enumerated in parallel with uploading items, and progress items
 * are recorded in order to support operation management.
 * @beta
 */
export function uploadEntryBatch(payload: IUploadEntryBatchPayload): IItemCacheThunkAction<Promise<void>> {
  const {
    parentItemKey,
    sourceEntryKey,
    callbacks = EMPTY_CALLBACKS,
    options = {}
  } = payload;

  return async (dispatch: IItemCacheDispatch) => {
    dispatch(progressAddon);

    const owner = {};

    dispatch(registerItemDemands({
      owner,
      items: {
        [sourceEntryKey]: {}
      }
    }));

    const cancelSource = new CancelSource();

    if (payload.cancelToken) {
      payload.cancelToken.subscribe(cancelSource.cancel);
    }

    const uploader = await dispatch(getEntryUploader({
      sourceEntryKey
    }));

    const {
      completed
    } = await dispatch(uploader({
      parentItemKey,
      cancelToken: cancelSource.token,
      callbacks,
      options
    }));

    try {
      await completed;
    } finally {
      cancelSource.dispose();

      dispatch(removeItemDemands({
        owner
      }));
    }
  };
}

interface IUploadActionPayload {
  parentItemKey: string;
  cancelToken: CancelToken;
  callbacks: IUploadCallbacks;
  options: IUploadItemOptions;
}

interface IUploadActionCreator extends IAsyncActionCreator<IUploadActionPayload, IItemCacheThunkAction<{
  started: Promise<void>;
  completed: Promise<void>;
}>> { }

function getChildUploaders(payload: {
  sourceEntryKey: string;
  cancelToken: CancelToken;
}): IItemCacheThunkAction<AsyncIterable<IUploadActionCreator>> {
  const {
    sourceEntryKey,
    cancelToken
  } = payload;

  const parentUploadProgressKey = getUploadProgressKey(sourceEntryKey);

  return async function* (dispatch: IItemCacheDispatch): AsyncIterable<IUploadActionCreator> { // tslint:disable-line:no-function-expression max-line-length
    const owner = {};

    dispatch(registerItemDemands({
      owner,
      items: {
        [parentUploadProgressKey]: {}
      }
    }));

    try {
      dispatch(resolveItems({
        items: {
          [parentUploadProgressKey]: {
            ...childItemsFacet.pack({
              itemKeys: []
            })
          }
        }
      }));

      for await (const childKey of dispatch(getChildKeys({
        itemKey: sourceEntryKey,
        cancelToken
      }))) {
        cancelToken.throwIfCanceled();

        const uploadProgressKey = getUploadProgressKey(childKey);

        dispatch(resolveItems({
          items: {
            [parentUploadProgressKey]: pack({
              childItems: childItemsFacet
            }, {
                childItems: {
                  itemKeys: new ArrayConcatIfNotMatchKeyPatch([uploadProgressKey], (i: string) => i)
                }
              })
          }
        }));

        yield dispatch(getEntryUploader({
          sourceEntryKey: childKey
        }));
      }
    } finally {
      dispatch(removeItemDemands({
        owner
      }));
    }
  };
}

/**
 * Gets an async generator for the child items of an item's default set.
 * This generator will emit each block of items as it expands each page, until all pages are expanded.
 */
function getChildKeys(payload: {
  itemKey: string;
  cancelToken: CancelToken;
}): IItemCacheThunkAction<AsyncIterable<string>> {
  const {
    itemKey,
    cancelToken
  } = payload;

  return async function* (dispatch: IItemCacheDispatch): AsyncIterable<string> { // tslint:disable-line:no-function-expression max-line-length
    const observedItems: { [itemKey: string]: true; } = {};

    const itemSetKey = await dispatch(demandItems((itemCache: IItemCache) => {
      const defaultChildItemSet = itemCache.demandItemFacet(defaultChildItemSetFacet, itemKey);

      return defaultChildItemSet && defaultChildItemSet.itemKey;
    }));

    if (!itemSetKey) {
      return;
    }

    for await (const itemKeys of dispatch(observeItemsUntilResolved((itemCache: IItemCache) => {
      if (cancelToken) {
        cancelToken.throwIfCanceled();
      }

      const childItems = itemCache.demandItemFacet(childItemsFacet, itemSetKey);

      const itemKeysToReturn: string[] = [];

      if (childItems) {
        for (const childItemKey of childItems.itemKeys) {
          const isPage = itemCache.isItemFacetResolved(pageFacet, childItemKey);

          if (isPage) {
            itemCache.demandItemFacet(childItemsFacet, childItemKey);
          } else {
            itemKeysToReturn.push(childItemKey);
          }
        }
      }

      return itemKeysToReturn;
    }))) {
      for (const itemKeyToReturn of itemKeys) {
        // Emit each new key which has not already been emitted.
        if (!(itemKeyToReturn in observedItems)) {
          yield itemKeyToReturn;

          observedItems[itemKeyToReturn] = true;
        }
      }
    }
  };
}

/**
 * Creates an action which, when dispatched, produces an action creator to upload a given entry
 * once it receives the information about its parent item.
 */
function getEntryUploader(payload: {
  sourceEntryKey: string;
}): IItemCacheThunkAction<Promise<IUploadActionCreator>> {
  const {
    sourceEntryKey
  } = payload;
  return async (dispatch: IItemCacheDispatch): Promise<IUploadActionCreator> => {
    const entryBatch = await dispatch(demandItems((itemCache: IItemCache) => {
      return itemCache.demandItemFacet(entryBatchFacet, sourceEntryKey);
    }));

    if (entryBatch) {
      return createEntryBatchUploader(sourceEntryKey);
    }

    const folder = await dispatch(demandItems((itemCache: IItemCache) => {
      return itemCache.demandItemFacet(folderFacet, sourceEntryKey);
    }));

    if (folder) {
      return createFolderEntryUploader(sourceEntryKey);
    } else {
      return createFileEntryUploader(sourceEntryKey);
    }
  };
}

function createEntryBatchUploader(sourceEntryKey: string): IUploadActionCreator {
  return createAsyncActionCreator((payload: IUploadActionPayload) => {
    const {
      parentItemKey,
      callbacks,
      options
    } = payload;

    const uploadProgressKey = getUploadProgressKey(sourceEntryKey);

    return (dispatch: IItemCacheDispatch) => {
      const cancelSource = new CancelSource();

      payload.cancelToken.subscribe(cancelSource.cancel);

      const owner = {};

      dispatch(registerItemDemands({
        owner: {},
        items: {
          [uploadProgressKey]: {}
        }
      }));

      dispatch(resolveItems({
        items: {
          [progressRootItemKey]: pack({
            childItems: childItemsFacet
          }, {
              childItems: {
                itemKeys: new ArrayConcatIfNotMatchKeyPatch([uploadProgressKey], (i: string) => i)
              }
            }),
          [uploadProgressKey]: {
            ...isGroupFacet.pack(true),
            ...cancelSourceFacet.pack(cancelSource),
            ...sourceItemFacet.pack({
              itemKey: sourceEntryKey
            }),
            ...destinationItemFacet.pack({
              itemKey: parentItemKey
            })
          }
        }
      }));

      const uploadersCreated = (async () => {
        const startedWork: Promise<void>[] = [];
        const completedWork: Promise<void>[] = [];

        for await (const uploader of dispatch(getChildUploaders({
          sourceEntryKey,
          cancelToken: cancelSource.token
        }))) {
          cancelSource.token.throwIfCanceled();

          const {
            started: childStarted, // tslint:disable-line:no-use-before-declare
            completed: childCompleted // tslint:disable-line:no-use-before-declare
          } = dispatch(uploader({
            parentItemKey,
            cancelToken: cancelSource.token,
            callbacks,
            options
          }));

          startedWork.push(childStarted);
          completedWork.push(childCompleted);
        }

        return {
          startedWork,
          completedWork
        };
      })();

      const started = (async () => {
        await Promise.race((await uploadersCreated).startedWork);
      })();
      const completed = (async () => {
        await waitForAll((await uploadersCreated).completedWork);
      })();

      (async () => {
        callbacks.onEntryBatchCreated({
          progressItemKey: uploadProgressKey
        });

        try {
          await completed;

          callbacks.onEntryBatchUploaded({
            progressItemKey: uploadProgressKey
          });
        } catch (e) {
          callbacks.onEntryBatchFailed({
            error: e,
            progressItemKey: uploadProgressKey
          });
        } finally {
          dispatch(resolveItems({
            items: {
              [uploadProgressKey]: pack({
                cancelSource: cancelSourceFacet
              }, {
                  cancelSource: new Delete<CancelSource>()
                })
            }
          }));

          dispatch(removeItemDemands({
            owner
          }));
        }
      })();

      return {
        started,
        completed
      };
    };
  });
}

/**
 * Creates an action which, when dispatched, produces an action creator to upload a folder entry
 * once it receives the information about its parent item.
 */
function createFolderEntryUploader(sourceEntryKey: string): IUploadActionCreator {
  return createAsyncActionCreator((payload: IUploadActionPayload) => {
    const {
      parentItemKey,
      callbacks,
      options
    } = payload;

    const uploadProgressKey = getUploadProgressKey(sourceEntryKey);

    return (dispatch: IItemCacheDispatch) => {
      const cancelSource = new CancelSource();

      payload.cancelToken.subscribe(cancelSource.cancel);

      const owner = {};

      dispatch(registerItemDemands({
        owner,
        items: {
          [uploadProgressKey]: {},
          [sourceEntryKey]: {}
        }
      }));

      dispatch(resolveItems({
        items: {
          [uploadProgressKey]: {
            ...errorFacet.pack(undefined),
            ...progressPhaseFacet.pack(ProgressPhase.created),
            ...isGroupFacet.pack(true),
            ...destinationItemFacet.pack({
              itemKey: parentItemKey
            }),
            ...sourceItemFacet.pack({
              itemKey: sourceEntryKey
            }),
            ...cancelSourceFacet.pack(cancelSource)
          }
        }
      }));

      const {
        started: createFolderStarted, // tslint:disable-line:no-use-before-declare
        itemCreated
      } = dispatch(uploadFolderEntry({
        sourceEntryKey,
        parentItemKey,
        cancelToken: cancelSource.token
      }));

      const uploadersCreated = (async () => {
        const itemKey = await itemCreated;

        const startedWork: Promise<void>[] = [createFolderStarted];
        const completedWork: Promise<void>[] = [];

        for await (const uploader of dispatch(getChildUploaders({
          sourceEntryKey,
          cancelToken: cancelSource.token
        }))) {
          const {
            started: childStarted,
            completed: childCompleted
          } = dispatch(uploader({
            parentItemKey: itemKey,
            cancelToken: cancelSource.token,
            callbacks,
            options
          }));

          startedWork.push(childStarted);
          completedWork.push(childCompleted);
        }

        return {
          startedWork,
          completedWork
        };
      })();

      const started = (async () => {
        await Promise.race((await uploadersCreated).startedWork);
      })();
      const completed = (async () => {
        await waitForAll((await uploadersCreated).completedWork);
      })();

      (async () => {
        callbacks.onItemQueued({
          progressItemKey: uploadProgressKey
        });

        try {
          await completed;

          callbacks.onItemUploaded({
            progressItemKey: uploadProgressKey
          });
        } catch (e) {
          callbacks.onItemFailed({
            progressItemKey: uploadProgressKey
          });
        } finally {
          dispatch(resolveItems({
            items: {
              [uploadProgressKey]: pack({
                cancelSource: cancelSourceFacet
              }, {
                  cancelSource: new Delete<CancelSource>()
                })
            }
          }));

          cancelSource.dispose();

          dispatch(removeItemDemands({
            owner
          }));
        }
      })();

      dispatch(trackItemProgress({
        started,
        completed: (async () => { await itemCreated; })(),
        progressItemKey: uploadProgressKey
      }));

      return {
        started,
        completed
      };
    };
  });
}

/**
 * Creates an action which, when dispatched. produces an action creator to upload a file entry
 * once it receives the information about its parent item.
 */
function createFileEntryUploader(sourceEntryKey: string): IUploadActionCreator {
  return createAsyncActionCreator((payload: IUploadActionPayload) => {
    const {
      parentItemKey,
      callbacks,
      options
    } = payload;

    const uploadProgressKey = getUploadProgressKey(sourceEntryKey);

    return (dispatch: IItemCacheDispatch) => {
      const cancelSource = new CancelSource();

      payload.cancelToken.subscribe(cancelSource.cancel);

      const createdItem = dispatch(readItems((itemCache: IItemCache) => {
        return itemCache.demandItemFacet(createdItemFacet, uploadProgressKey);
      }));

      if (createdItem) {
        return {
          started: (async () => { /* Nothing */ })(),
          completed: (async () => { /* Nothing */ })()
        };
      }

      const owner = {};

      dispatch(registerItemDemands({
        owner,
        items: {
          [uploadProgressKey]: {},
          [sourceEntryKey]: {}
        }
      }));

      dispatch(resolveItems({
        items: {
          [uploadProgressKey]: {
            ...errorFacet.pack(undefined),
            ...progressPhaseFacet.pack(ProgressPhase.created),
            ...isGroupFacet.pack(false),
            ...destinationItemFacet.pack({
              itemKey: parentItemKey
            }),
            ...sourceItemFacet.pack({
              itemKey: sourceEntryKey
            }),
            ...cancelSourceFacet.pack(cancelSource)
          }
        }
      }));

      const {
        started,
        completed
      } = dispatch(uploadFileEntry({
        sourceEntryKey,
        parentItemKey,
        cancelToken: cancelSource.token,
        options
      }));

      (async () => {
        callbacks.onItemQueued({
          progressItemKey: uploadProgressKey
        });

        try {
          await completed;

          callbacks.onItemUploaded({
            progressItemKey: uploadProgressKey
          });
        } catch (e) {
          callbacks.onItemFailed({
            progressItemKey: uploadProgressKey
          });
        } finally {
          dispatch(resolveItems({
            items: {
              [uploadProgressKey]: pack({
                cancelSource: cancelSourceFacet
              }, {
                  cancelSource: new Delete<CancelSource>()
                })
            }
          }));

          cancelSource.dispose();

          dispatch(removeItemDemands({
            owner
          }));
        }
      })();

      dispatch(trackItemProgress({
        started,
        completed,
        progressItemKey: uploadProgressKey
      }));

      return {
        started,
        completed
      };
    };
  });
}

function uploadFolderEntry(payload: {
  sourceEntryKey: string;
  parentItemKey: string;
  cancelToken: CancelToken;
}): IItemCacheThunkAction<{
  started: Promise<void>;
  itemCreated: Promise<string>;
}> {
  return (dispatch: IItemCacheDispatch) => {
    const {
      sourceEntryKey,
      parentItemKey,
      cancelToken
    } = payload;

    const uploadProgressKey = getUploadProgressKey(sourceEntryKey);

    const results = (async () => {
      const fileName = await dispatch(demandItems((itemCache: IItemCache) => {
        return itemCache.demandItemFacet(fileNameFacet, sourceEntryKey);
      }));

      if (!fileName) {
        // TODO Invoke the control handler and use the name validator.
        throw new Error('Invalid folder name.');
      }

      const createFolderOptions: typeof createFolderOperation.type = {
        folders: {
          [fileName.name]: {
            progressItemKey: uploadProgressKey
          }
        }
      };

      dispatch(resolveItems({
        items: {
          [uploadProgressKey]: {
            ...createFolderOperation.pack(createFolderOptions)
          }
        }
      }));

      const {
        started: createFolderStarted,
        completed: createFolderCompleted
      } = dispatch(startOperation({
        operationTypeFacet: createFolderOperation,
        items: {
          [parentItemKey]: createFolderOptions
        }
      }));

      const dispose = cancelToken.subscribe(() => {
        dispatch(cancelOperation({
          operationTypeFacet: createFolderOperation,
          items: {
            [parentItemKey]: createFolderOptions
          }
        }));
      });

      (async () => {
        await createFolderStarted;

        dispose();
      })();

      return {
        started: createFolderStarted,
        itemCreated: (async () => {
          await createFolderCompleted;

          const createdItem = dispatch(readItems((itemCache: IItemCache) => {
            return itemCache.demandItemFacet(createdItemFacet, uploadProgressKey);
          }));

          const itemKey = createdItem && createdItem.itemKey;

          if (!itemKey) {
            throw new Error('No folder created.');
          }

          return itemKey;
        })()
      };
    })();

    return {
      started: (async () => {
        await (await results).started;
      })(),
      itemCreated: (async () => {
        return await (await results).itemCreated;
      })()
    };
  };
}

function uploadFileEntry(payload: {
  sourceEntryKey: string;
  parentItemKey: string;
  cancelToken: CancelToken;
  options: IUploadItemOptions;
}): IItemCacheThunkAction<{
  started: Promise<void>;
  completed: Promise<void>;
}> {
  const {
    sourceEntryKey,
    parentItemKey,
    cancelToken,
    options
  } = payload;

  const uploadProgressKey = getUploadProgressKey(sourceEntryKey);

  return (dispatch: IItemCacheDispatch) => {
    const uploadItemsOptions: typeof uploadItemsOperation.type = {
      sourceEntries: {
        [sourceEntryKey]: {
          progressItemKey: uploadProgressKey,
          ...options
        }
      }
    };

    const results = (async () => {
      const fileSize = await dispatch(demandItems((itemCache: IItemCache) => {
        return itemCache.demandItemFacet(fileSizeFacet, sourceEntryKey);
      }));

      if (typeof fileSize !== 'undefined') {
        dispatch(resolveItems({
          items: {
            [uploadProgressKey]: {
              ...progressSizeFacet.pack({
                total: fileSize,
                current: 0
              })
            }
          }
        }));
      }

      const {
        started: uploadItemsStarted,
        completed: uploadItemsCompleted
      } = dispatch(startOperation({
        operationTypeFacet: uploadItemsOperation,
        items: {
          [parentItemKey]: uploadItemsOptions
        }
      }));

      const dispose = cancelToken.subscribe(() => {
        dispatch(cancelOperation({
          operationTypeFacet: uploadItemsOperation,
          items: {
            [parentItemKey]: uploadItemsOptions
          }
        }));
      });

      (async () => {
        await uploadItemsStarted;

        dispose();
      })();

      return {
        started: uploadItemsStarted,
        completed: uploadItemsCompleted
      };
    })();

    return {
      started: (async () => (await results).started)(),
      completed: (async () => (await results).completed)()
    };
  };
}
